import React from "react";
import { Box, Chip, Dialog, Tab, Tabs, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { OralModel, oralModelToDetails } from "../../../../../models/Oral.model";
import { OralItem } from "./oral-item";
import { OralDetailDrawer } from "../../../../../components/oral/OralDetailDrawer";
import { AnnotationDialog } from "../../../actives/annotation.dialog";
import { UserClassificationsDialog } from "./userClassifications.dialog";
import { SimpleSnackbar } from "../../../../../components/snackbar";
import { Loading } from "../../../../../components/loading";
import { WizardShell } from "../../../../../components/ui";
import { ORAL_STEPS } from "../../wizard-steps";
import { useActivesOral } from "./use-actives-oral";
import OralTabPanel from "./components/OralTabPanel";

type Props = {
    onFinish: () => void;
    onBack: () => void;
};

export const ActivesOral: React.FC<Props> = ({ onFinish, onBack }) => {
    const theme = useTheme();
    const {
        loading,
        oralActives,
        value,
        filter,
        setFilter,
        open,
        setOpen,
        selectedOral,
        activeOral,
        onPopUp,
        annotationOpen,
        setAnnotationOpen,
        classificationsOpen,
        setClassificationsOpen,
        activeSelected,
        text,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        filterKey,
        setFilterKey,
        classes,
        selectedClasses,
        filteredAll,
        favorites,
        selectedCount,
        handleChange,
        handleClickViewActive,
        handleFavorite,
        handleSelectActive,
        handleContinue,
        handleClickOpen,
        handleClassification,
        popUpOpen,
        handleClose,
        saveAnnotation,
        classificationSave,
        deleteClassification,
        handleSelectedClasses,
        orderedFiltered,
    } = useActivesOral(onFinish);

    const renderItem = (oral: OralModel, index: number, onPopUpProp: () => unknown) => (
        <OralItem
            key={index}
            props={oral}
            onClick={() => handleClickViewActive(oral)}
            onPopUp={onPopUpProp}
            annotationOpen={(model) => handleClickOpen(model)}
            classificationOpen={(model) => handleClassification(model)}
            onSelect={handleSelectActive}
            onDelete={deleteClassification}
            onFavorite={handleFavorite}
            checked={!!selectedOral.find((prod) => prod.id === oral.id)}
        />
    );

    return (
        <>
            <Loading open={loading} />
            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <OralDetailDrawer
                open={open}
                detail={oralModelToDetails(activeOral)}
                onClose={() => setOpen(false)}
                onFavorite={() => handleFavorite(activeOral)}
            />

            {classificationsOpen && (
                <UserClassificationsDialog
                    open={classificationsOpen}
                    onClose={() => setClassificationsOpen(false)}
                    externalObservations={text}
                    onSaveClassification={classificationSave}
                />
            )}

            {annotationOpen && (
                <AnnotationDialog
                    annotation={activeSelected?.annotation}
                    open={annotationOpen}
                    onSaveAnnotation={saveAnnotation}
                    onClose={() => setAnnotationOpen(false)}
                />
            )}

            {onPopUp && (
                <Dialog open={onPopUp} onClose={handleClose}>
                    <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                        {"Opção não disponível."}
                        <CloseIcon sx={{ cursor: "pointer" }} onClick={handleClose} />
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Você não pode fazer anotações no ativo selecionado. Volte para a listagem de todos ativos para incluir anotações.
                        </DialogContentText>
                    </DialogContent>
                </Dialog>
            )}

            <WizardShell
                steps={ORAL_STEPS as unknown as string[]}
                activeIndex={2}
                onBack={onBack}
                onNext={handleContinue}
                nextLabel="Continuar"
                nextDisabled={selectedCount === 0}
            >
                {/* Cabeçalho */}
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", mb: "4px" }}>
                    <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400, color: "text.primary" }}>
                        Ativos Orais
                    </Typography>
                    <Chip
                        label={`${selectedCount} selecionado${selectedCount !== 1 ? "s" : ""}`}
                        size="small"
                        sx={{
                            backgroundColor: theme.palette.brand.cream,
                            border: `1px solid ${theme.palette.brand.heroBorder}`,
                            color: "primary.dark",
                            fontWeight: 600,
                            fontSize: "12px",
                            height: 24,
                        }}
                    />
                </Box>
                <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: "16px" }}>
                    Selecione os ativos orais para a formulação.
                </Typography>

                {/* Tabs */}
                <Tabs
                    value={value}
                    onChange={handleChange}
                    sx={{
                        mb: "12px",
                        "& .MuiTab-root": { fontSize: "13.5px", fontWeight: 600, textTransform: "none", minHeight: 40 },
                        "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: 2 },
                    }}
                >
                    <Tab label="Todos" />
                    <Tab label="Favoritos" />
                    <Tab label="Selecionados" />
                </Tabs>

                {/* Listas com filtros */}
                <Box>
                    <OralTabPanel
                        value={value}
                        index={0}
                        filter={filter}
                        setFilter={setFilter}
                        filterKey={filterKey}
                        setFilterKey={setFilterKey}
                        uniqueActives={classes}
                        onApplyFilters={handleSelectedClasses}
                        previousFilters={selectedClasses}
                    >
                        {filteredAll.map((oral, index) => renderItem(oral, index, () => onPopUp))}
                    </OralTabPanel>

                    <OralTabPanel value={value} index={1} filter={filter} setFilter={setFilter} filterKey={filterKey} setFilterKey={setFilterKey}>
                        {orderedFiltered(favorites).map((oral, index) => renderItem(oral, index, () => popUpOpen))}
                    </OralTabPanel>

                    <OralTabPanel value={value} index={2} filter={filter} setFilter={setFilter} filterKey={filterKey} setFilterKey={setFilterKey}>
                        {orderedFiltered(selectedOral).map((oral, index) => renderItem(oral, index, () => popUpOpen()))}
                    </OralTabPanel>
                </Box>
            </WizardShell>
        </>
    );
};
